<app-modal
  [isOpen]="isOpen"
  (close)="cerrar()"
  className="max-w-3xl mx-4 sm:mx-6"
>
  @if (loading) {
    <div class="p-12 flex flex-col items-center justify-center gap-3">
      <div
        class="animate-spin rounded-full h-7 w-7 border-b-2 border-brand-500"
      ></div>
      <p class="text-sm text-gray-500 dark:text-gray-400">Cargando tarea...</p>
    </div>
  } @else if (error) {
    <div class="p-6">
      <div
        class="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10"
      >
        <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
      </div>
    </div>
  } @else if (task) {
    <div
      class="p-6 sm:p-8"
      [class.bg-error-50/50]="isCancelled"
      [class.dark:bg-error-900/5]="isCancelled"
    >
      <!-- ====== Banner de cancelación ====== -->
      @if (isCancelled) {
        <div
          class="mb-6 rounded-xl border-2 border-error-300 dark:border-error-500/40 bg-error-50 dark:bg-error-500/10 overflow-hidden"
        >
          <div class="flex items-start gap-4 p-4">
            <div
              class="shrink-0 flex h-12 w-12 items-center justify-center rounded-full bg-error-500 text-white shadow-md"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap mb-1">
                <span
                  class="inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md bg-error-500 text-white"
                >
                  Cancelada
                </span>
                @if (task.datetimeUpdated) {
                  <span class="text-xs text-error-700 dark:text-error-300">{{
                    formatDate(task.datetimeUpdated)
                  }}</span>
                }
              </div>
              <p
                class="text-sm font-semibold text-error-800 dark:text-error-200"
              >
                @if (cancelReasonName) {
                  Motivo: {{ cancelReasonName }}
                } @else {
                  Esta tarea fue cancelada
                }
              </p>
              @if (task.cancelNotes) {
                <p
                  class="mt-1.5 text-sm text-error-700 dark:text-error-300 whitespace-pre-line"
                >
                  {{ task.cancelNotes }}
                </p>
              }
            </div>
          </div>
        </div>
      }

      <!-- ====== Header con avatar ====== -->
      <div class="flex items-start gap-6 mb-8">
        <!-- Avatar -->
        <div class="shrink-0">
          <div
            class="flex h-20 w-20 items-center justify-center rounded-full {{
              avatarColorClass
            }}"
            [ngStyle]="avatarStyle"
          >
            <span class="text-2xl font-semibold">{{ initials }}</span>
          </div>
        </div>

        <!-- Title + chips -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap mb-1.5">
            @if (task.type) {
              <span
                class="text-xs px-2 py-0.5 rounded-md text-white font-medium"
                [style.background-color]="task.type.color || '#6B7280'"
              >
                {{ task.type.label }}
              </span>
            }
            @if (task.status) {
              <span
                class="text-xs px-2 py-0.5 rounded-full font-medium"
                [style.background-color]="
                  (task.status.color || '#9CA3AF') + '22'
                "
                [style.color]="task.status.color || '#6B7280'"
              >
                {{ task.status.label }}
              </span>
            }
            @if (task.priority) {
              <span
                class="text-xs px-2 py-0.5 rounded-full font-medium"
                [style.background-color]="
                  (task.priority.color || '#9CA3AF') + '22'
                "
                [style.color]="task.priority.color || '#6B7280'"
              >
                {{ task.priority.label }}
              </span>
            }
            @if (task.isProtected) {
              <span
                class="text-[10px] px-1.5 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium"
              >
                🔒 Protegida
              </span>
            }
            @if (isOverdue(task)) {
              <span
                class="text-[10px] px-1.5 py-0.5 rounded-md bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400 font-medium uppercase tracking-wider"
              >
                ⚠ Vencida
              </span>
            }
          </div>
          <h2
            class="text-2xl font-bold mb-1 wrap-break-words"
            [class.text-gray-900]="!isCancelled"
            [class.dark:text-white]="!isCancelled"
            [class.text-gray-500]="isCancelled"
            [class.dark:text-gray-400]="isCancelled"
            [class.line-through]="isCancelled"
            [class.decoration-error-500]="isCancelled"
            [class.decoration-2]="isCancelled"
          >
            {{ task.title }}
          </h2>
          @if (task.category) {
            <p class="text-base text-gray-600 dark:text-gray-400">
              {{ task.category.name }}
            </p>
          }
        </div>

        <!-- Owner -->
        <div class="mt-auto pt-4">
          <div class="flex items-center gap-2 justify-end">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300"
              >Responsable:</span
            >
            @if (loadingUsers) {
              <div
                class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"
              ></div>
            } @else if (assignedUser) {
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                formatUserName(assignedUser)
              }}</span>
            } @else {
              <span class="text-sm text-gray-400 dark:text-gray-500"
                >Sin información</span
              >
            }
          </div>
        </div>
      </div>

      <!-- ====== Action bar ====== -->
      <div
        class="flex items-center gap-2 flex-wrap mb-6 pb-6 border-b border-gray-200 dark:border-gray-700"
      >
        @if (!task.status?.isFinal) {
          <button
            type="button"
            (click)="completar()"
            *appHasPermission="'tasks:edit'"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-success-500 text-white hover:bg-success-600 transition-colors"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            Completar
          </button>
          <button
            type="button"
            (click)="posponer()"
            *appHasPermission="'tasks:edit'"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-warning-500 text-white hover:bg-warning-600 transition-colors"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Posponer 1 día
          </button>
          <button
            type="button"
            (click)="abrirCancelDialog()"
            *appHasPermission="'tasks:edit'"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Cancelar
          </button>
        } @else {
          <button
            type="button"
            (click)="reabrir()"
            *appHasPermission="'tasks:edit'"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-500 text-white hover:bg-gray-600 transition-colors"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Reabrir
          </button>
        }
        <button
          type="button"
          (click)="editar()"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors ml-auto"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          Editar
        </button>
        <app-quick-actions-menu
          label="Acciones"
          align="right"
          [contactId]="primaryContact?.contactId"
          [contactName]="primaryContactName"
          [contactEmail]="primaryContact?.contactMail"
          [contactPhone]="primaryContact?.contactPhone"
          [companyId]="task.intIdCompany"
          [companyName]="resolvedCompanyName"
          [dealId]="task.intIdDeal"
          [dealName]="resolvedDealName"
          [leadId]="task.intIdLead"
          [leadName]="resolvedLeadName"
          (actionTriggered)="cerrar()"
        >
        </app-quick-actions-menu>
      </div>

      <!-- ====== Tabs ====== -->
      <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav class="-mb-px flex gap-1 overflow-x-auto">
          <button
            (click)="setTab('info')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'info'"
            [class.text-brand-600]="activeTab === 'info'"
            [class.dark:text-brand-400]="activeTab === 'info'"
            [class.border-transparent]="activeTab !== 'info'"
            [class.text-gray-500]="activeTab !== 'info'"
            [class.dark:text-gray-400]="activeTab !== 'info'"
          >
            Información
          </button>
          <button
            (click)="setTab('subtasks')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'subtasks'"
            [class.text-brand-600]="activeTab === 'subtasks'"
            [class.dark:text-brand-400]="activeTab === 'subtasks'"
            [class.border-transparent]="activeTab !== 'subtasks'"
            [class.text-gray-500]="activeTab !== 'subtasks'"
            [class.dark:text-gray-400]="activeTab !== 'subtasks'"
          >
            Subtareas ({{ subtasks.length }})
          </button>
          <button
            (click)="setTab('comments')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'comments'"
            [class.text-brand-600]="activeTab === 'comments'"
            [class.dark:text-brand-400]="activeTab === 'comments'"
            [class.border-transparent]="activeTab !== 'comments'"
            [class.text-gray-500]="activeTab !== 'comments'"
            [class.dark:text-gray-400]="activeTab !== 'comments'"
          >
            Comentarios ({{ comments.length }})
          </button>
          <button
            (click)="setTab('dependencies')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'dependencies'"
            [class.text-brand-600]="activeTab === 'dependencies'"
            [class.dark:text-brand-400]="activeTab === 'dependencies'"
            [class.border-transparent]="activeTab !== 'dependencies'"
            [class.text-gray-500]="activeTab !== 'dependencies'"
            [class.dark:text-gray-400]="activeTab !== 'dependencies'"
          >
            Dependencias ({{ dependencies.length }})
          </button>
          <button
            (click)="setTab('timeline')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'timeline'"
            [class.text-brand-600]="activeTab === 'timeline'"
            [class.dark:text-brand-400]="activeTab === 'timeline'"
            [class.border-transparent]="activeTab !== 'timeline'"
            [class.text-gray-500]="activeTab !== 'timeline'"
            [class.dark:text-gray-400]="activeTab !== 'timeline'"
          >
            Línea de Tiempo
          </button>
        </nav>
      </div>

      <!-- =================== TAB: INFO =================== -->
      @if (activeTab === 'info') {
        <div class="space-y-6">
          <!-- Descripción -->
          @if (task.description) {
            <div>
              <h3
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
              >
                Descripción
              </h3>
              <p
                class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line"
              >
                {{ task.description }}
              </p>
            </div>
          }

          <!-- Programación -->
          <div
            [class.pt-4]="task.description"
            [class.border-t]="task.description"
            [class.border-gray-200]="task.description"
            [class.dark:border-gray-700]="task.description"
          >
            <h3
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Programación
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Fecha vencimiento -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5"
                    [class.text-error-500]="isOverdue(task)"
                    [class.text-gray-400]="!isOverdue(task)"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Vencimiento
                  </p>
                  <p
                    class="text-sm font-medium"
                    [class.text-error-600]="isOverdue(task)"
                    [class.dark:text-error-400]="isOverdue(task)"
                    [class.text-gray-900]="!isOverdue(task)"
                    [class.dark:text-white]="!isOverdue(task)"
                  >
                    {{ formatDate(task.datetimeDue) }}
                  </p>
                </div>
              </div>

              <!-- Duración estimada -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Duración estimada
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{
                      task.estimatedDurationMin
                        ? task.estimatedDurationMin + ' min'
                        : '—'
                    }}
                  </p>
                </div>
              </div>

              @if (task.actualDurationMin) {
                <div class="flex items-start gap-3">
                  <div class="shrink-0 mt-0.5">
                    <svg
                      class="w-5 h-5 text-success-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Duración real
                    </p>
                    <p
                      class="text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {{ task.actualDurationMin }} min
                    </p>
                  </div>
                </div>
              }

              @if (task.datetimeCompleted) {
                <div class="flex items-start gap-3">
                  <div class="shrink-0 mt-0.5">
                    <svg
                      class="w-5 h-5 text-success-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Completada el
                    </p>
                    <p
                      class="text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {{ formatDate(task.datetimeCompleted) }}
                    </p>
                  </div>
                </div>
              }

              <!-- Subtarea progress (si tiene) -->
              @if (task.subtaskCount && task.subtaskCount > 0) {
                <div class="flex items-start gap-3 sm:col-span-2">
                  <div class="shrink-0 mt-0.5">
                    <svg
                      class="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"
                      />
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Progreso de subtareas
                    </p>
                    <div class="flex items-center gap-2 mt-1">
                      <div
                        class="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden max-w-xs"
                      >
                        <div
                          class="h-full bg-brand-500 rounded-full transition-all"
                          [style.width.%]="
                            task.subtaskCount
                              ? (100 * (task.subtaskCompletedCount || 0)) /
                                task.subtaskCount
                              : 0
                          "
                        ></div>
                      </div>
                      <span
                        class="text-sm font-semibold text-gray-700 dark:text-gray-300"
                      >
                        {{ task.subtaskCompletedCount || 0 }}/{{
                          task.subtaskCount
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Asociaciones -->
          @if (associationCount() > 0) {
            <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
              >
                Asociaciones
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                @if (task.intIdCompany) {
                  <div class="flex items-start gap-3">
                    <div class="shrink-0 mt-0.5">
                      <svg
                        class="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-xs text-gray-500 dark:text-gray-400">
                        Empresa
                      </p>
                      <p
                        class="text-sm font-medium text-gray-900 dark:text-white"
                      >
                        <span [title]="'Identificador interno: ' + task.intIdCompany">{{ resolvedCompanyName || 'Empresa sin nombre disponible' }}</span>
                      </p>
                    </div>
                  </div>
                }
                @if (task.intIdDeal) {
                  <div class="flex items-start gap-3">
                    <div class="shrink-0 mt-0.5">
                      <svg
                        class="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-xs text-gray-500 dark:text-gray-400">
                        Negocio (Deal)
                      </p>
                      <p
                        class="text-sm font-medium text-gray-900 dark:text-white"
                      >
                        <span [title]="'Identificador interno: ' + task.intIdDeal">{{ resolvedDealName || 'Negocio sin nombre disponible' }}</span>
                      </p>
                    </div>
                  </div>
                }
                @if (task.intIdLead) {
                  <div class="flex items-start gap-3">
                    <div class="shrink-0 mt-0.5">
                      <svg
                        class="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-xs text-gray-500 dark:text-gray-400">
                        Creado desde lead
                      </p>
                      <button
                        type="button"
                        (click)="goToLead(task.intIdLead)"
                        class="text-sm font-medium text-brand-600 dark:text-brand-400 hover:underline text-left"
                      >
                        {{ resolvedLeadName || 'Lead #' + task.intIdLead }}
                      </button>
                    </div>
                  </div>
                }
                @if (task.idParentTask) {
                  <div class="flex items-start gap-3">
                    <div class="shrink-0 mt-0.5">
                      <svg
                        class="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5"
                        />
                      </svg>
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-xs text-gray-500 dark:text-gray-400">
                        Tarea padre
                      </p>
                      <p
                        class="text-sm font-medium text-gray-900 dark:text-white"
                      >
                        <span [title]="'Identificador interno: ' + task.idParentTask">{{ resolvedParentTaskTitle || 'Tarea sin título disponible' }}</span>
                      </p>
                    </div>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Contactos asociados -->
          @if (contacts.length > 0) {
            <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
              >
                Contactos asociados ({{ contacts.length }})
              </h3>
              <div class="space-y-3">
                @for (c of contacts; track c.id) {
                  <div
                    class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50"
                  >
                    <div
                      class="shrink-0 flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold {{
                        getEntityAvatarColor(c.contactFullname || '')
                      }}"
                    >
                      {{ getEntityInitials(c.contactFullname) }}
                    </div>
                    <div class="min-w-0 flex-1">
                      <div
                        class="text-sm font-medium text-gray-900 dark:text-white"
                      >
                        {{ c.contactFullname }} {{ c.contactLastname || '' }}
                      </div>
                      <div
                        class="flex items-center gap-3 mt-0.5 flex-wrap text-xs text-gray-500 dark:text-gray-400"
                      >
                        @if (c.contactMail) {
                          <span>{{ c.contactMail }}</span>
                        }
                        @if (c.contactMail && c.contactPhone) {
                          <span>·</span>
                        }
                        @if (c.contactPhone) {
                          <span>{{ c.contactPhone }}</span>
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Followers -->
          @if (followers.length > 0) {
            <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
              >
                Seguidores ({{ followers.length }})
              </h3>
              <div class="flex flex-wrap gap-2">
                @for (f of followers; track f.id) {
                  <span
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs"
                  >
                    <div
                      class="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold {{
                        getEntityAvatarColor(f.userFullname || '')
                      }}"
                    >
                      {{ getEntityInitials(f.userFullname) }}
                    </div>
                    <span class="font-medium text-gray-800 dark:text-gray-100">
                      {{ f.userFullname }} {{ f.userLastname || '' }}
                    </span>
                  </span>
                }
              </div>
            </div>
          }

          <!-- Resultado (cuando completada) -->
          @if (task.completionResult || task.completionNotes) {
            <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
              >
                Resultado de la completación
              </h3>
              @if (task.completionResult) {
                <p class="text-sm font-semibold text-gray-900 dark:text-white">
                  {{ task.completionResult }}
                </p>
              }
              @if (task.completionNotes) {
                <p
                  class="text-sm text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-line"
                >
                  {{ task.completionNotes }}
                </p>
              }
            </div>
          }

          <!-- Timestamps -->
          <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Fecha de creación
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ formatDate(task.datetimeCreated) }}
                  </p>
                </div>
              </div>

              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Creado por
                  </p>
                  @if (loadingUsers) {
                    <div
                      class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500 mt-1"
                    ></div>
                  } @else {
                    <p
                      class="text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {{ formatUserName(creatorUser) }}
                    </p>
                  }
                </div>
              </div>

              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Última actualización
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ formatDate(task.datetimeUpdated) }}
                  </p>
                </div>
              </div>

              @if (task.autoCompleteWhenSubtasksDone) {
                <div class="flex items-start gap-3">
                  <div class="shrink-0 mt-0.5">
                    <svg
                      class="w-5 h-5 text-brand-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Comportamiento
                    </p>
                    <p
                      class="text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Auto-completa al terminar subtareas
                    </p>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      }

      <!-- =================== TAB: SUBTASKS =================== -->
      @if (activeTab === 'subtasks') {
        <div class="space-y-4">
          <div class="flex justify-between items-center">
            <h3
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
            >
              {{ subtasks.length }} subtarea(s)
              @if (task.subtaskCount && task.subtaskCount > 0) {
                <span class="ml-1 text-gray-400"
                  >— {{ task.subtaskCompletedCount || 0 }} completadas</span
                >
              }
            </h3>
            <button
              type="button"
              (click)="crearSubtarea()"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors"
            >
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Nueva subtarea
            </button>
          </div>

          @if (subtasks.length === 0) {
            <div class="text-center py-10">
              <svg
                class="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"
                />
              </svg>
              <p class="text-sm text-gray-400 dark:text-gray-500">
                Esta tarea no tiene subtareas.
              </p>
            </div>
          } @else {
            <div class="space-y-2">
              @for (s of subtasks; track s.id) {
                <button
                  type="button"
                  (click)="abrirSubtarea(s)"
                  class="w-full flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  <div
                    class="shrink-0 w-2 h-2 rounded-full"
                    [style.background-color]="s.status?.color || '#9CA3AF'"
                  ></div>
                  <div class="flex-1 min-w-0">
                    <p
                      class="text-sm font-medium text-gray-900 dark:text-white truncate"
                    >
                      {{ s.title }}
                    </p>
                    <div
                      class="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400 flex-wrap"
                    >
                      @if (s.status) {
                        <span>{{ s.status.label }}</span>
                      }
                      @if (s.datetimeDue) {
                        <span>· {{ formatDate(s.datetimeDue) }}</span>
                      }
                      @if (s.priority) {
                        <span>· {{ s.priority.label }}</span>
                      }
                    </div>
                  </div>
                </button>
              }
            </div>
          }
        </div>
      }

      <!-- =================== TAB: COMMENTS =================== -->
      @if (activeTab === 'comments') {
        <div class="space-y-4">
          <!-- Editor -->
          <div
            class="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
          >
            <textarea
              [(ngModel)]="newCommentContent"
              rows="3"
              placeholder="Escribe un comentario..."
              class="w-full text-sm bg-transparent border-0 focus:ring-0 focus:outline-none resize-none dark:text-white/90 placeholder:text-gray-400"
            ></textarea>
            <div
              class="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-700/50"
            >
              <button
                type="button"
                (click)="agregarComentario()"
                [disabled]="!newCommentContent.trim()"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Enviar
              </button>
            </div>
          </div>

          @if (comments.length === 0) {
            <div class="text-center py-10">
              <svg
                class="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <p class="text-sm text-gray-400 dark:text-gray-500">
                No hay comentarios todavía.
              </p>
            </div>
          } @else {
            <ul class="space-y-3">
              @for (c of comments; track c.id) {
                <li
                  class="flex gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50"
                >
                  <div
                    class="shrink-0 flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold {{
                      getEntityAvatarColor(c.authorFullname || '')
                    }}"
                  >
                    {{ getEntityInitials(c.authorFullname) }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div
                      class="flex items-center justify-between gap-2 flex-wrap"
                    >
                      <div>
                        <span
                          class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                        >
                          {{ c.authorFullname }} {{ c.authorLastname || '' }}
                        </span>
                        <span
                          class="text-xs text-gray-500 dark:text-gray-400 ml-2"
                          >{{ formatDate(c.datetimeCreated) }}</span
                        >
                      </div>
                      <button
                        type="button"
                        (click)="eliminarComentario(c)"
                        class="text-xs text-error-600 hover:underline"
                      >
                        Eliminar
                      </button>
                    </div>
                    <p
                      class="text-sm text-gray-700 dark:text-gray-300 mt-1.5 whitespace-pre-line"
                    >
                      {{ c.content }}
                    </p>
                  </div>
                </li>
              }
            </ul>
          }
        </div>
      }

      <!-- =================== TAB: DEPENDENCIES (INC 29) =================== -->
      @if (activeTab === 'dependencies') {
        <div class="space-y-5">
          <!-- Agregar dependencia (bloqueada por) -->
          <div
            *appHasPermission="'tasks:edit'"
            class="rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <h3
              class="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1"
            >
              Agregar tarea bloqueante
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Esta tarea no podrá completarse hasta que las tareas bloqueantes
              estén finalizadas.
            </p>
            <div class="flex flex-col sm:flex-row sm:items-end gap-3">
              <div class="flex-1 min-w-0">
                <app-entity-picker
                  mode="single"
                  label="Bloqueada por"
                  placeholder="Buscar tarea por título... (Enter para ver todas)"
                  [selectedId]="newBlockedBy?.id ?? null"
                  [selectedLabel]="newBlockedBy?.label ?? null"
                  [selectedSublabel]="newBlockedBy?.sublabel ?? null"
                  [searchFn]="searchTaskFn"
                  (selectionChange)="onBlockedByChange($event)"
                />
              </div>
              <button
                type="button"
                (click)="agregarDependencia()"
                [disabled]="!newBlockedBy || addingDependency"
                class="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                <svg
                  class="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Agregar
              </button>
            </div>
          </div>

          <!-- Lista de tareas bloqueantes -->
          <div>
            <h3
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Bloqueada por ({{ dependencies.length }})
            </h3>
            @if (dependencies.length === 0) {
              <div class="text-center py-8">
                <svg
                  class="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
                <p class="text-sm text-gray-400 dark:text-gray-500">
                  Esta tarea no tiene dependencias bloqueantes.
                </p>
              </div>
            } @else {
              <div class="space-y-2">
                @for (d of dependencies; track d.id) {
                  <div
                    class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50"
                  >
                    <div class="shrink-0">
                      <svg
                        class="w-5 h-5"
                        [class.text-success-500]="d.blockedByIsFinal"
                        [class.text-warning-500]="!d.blockedByIsFinal"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        @if (d.blockedByIsFinal) {
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        } @else {
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        }
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p
                        class="text-sm font-medium text-gray-900 dark:text-white truncate"
                      >
                        <span [title]="'Identificador interno: ' + d.idTaskBlockedBy">{{ d.blockedByTaskTitle }}</span>
                      </p>
                      <p
                        class="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
                      >
                        Estado: {{ d.blockedByStatusName }}
                      </p>
                    </div>
                    <span
                      class="text-xs px-2 py-0.5 rounded-full font-medium"
                      [class]="
                        d.blockedByIsFinal
                          ? 'bg-success-100 text-success-700 dark:bg-success-500/10 dark:text-success-400'
                          : 'bg-warning-100 text-warning-700 dark:bg-warning-500/10 dark:text-warning-400'
                      "
                    >
                      {{ d.blockedByIsFinal ? 'Resuelta' : 'Pendiente' }}
                    </span>
                    <button
                      type="button"
                      *appHasPermission="'tasks:edit'"
                      (click)="eliminarDependencia(d)"
                      title="Quitar dependencia"
                      class="shrink-0 p-1.5 rounded-md text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"
                        />
                      </svg>
                    </button>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }

      <!-- =================== TAB: TIMELINE =================== -->

      @if (activeTab === 'timeline') {
        <app-audit-timeline
          [logs]="timelineLogs"
          [loading]="loadingTimeline"
          [error]="timelineError"
          [currentPage]="timelinePage"
          [totalPages]="timelineTotalPages"
          [totalElements]="timelineTotalElements"
          emptyMessage="No hay eventos de auditoría para esta tarea."
          (pageChange)="loadTimeline($event)"
        />
      }
    </div>

    <!-- ====== Cancel dialog (overlay interno) ====== -->
    @if (showCancelDialog) {
      <div
        class="absolute inset-0 z-10 bg-black/40 flex items-center justify-center p-6"
        (click)="showCancelDialog = false"
      >
        <div
          class="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 w-full max-w-md"
          (click)="$event.stopPropagation()"
        >
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Cancelar tarea
          </h3>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1"
            >Motivo</label
          >
          <select
            [(ngModel)]="cancelReasonId"
            class="w-full text-sm rounded-md border border-gray-300 bg-white text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white px-3 py-2 mb-3"
          >
            @for (r of cancelReasons; track r.id) {
              <option [ngValue]="r.id">{{ r.label || r.name }}</option>
            }
          </select>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1"
            >Notas (opcional)</label
          >
          <textarea
            [(ngModel)]="cancelNotes"
            rows="3"
            class="w-full text-sm rounded-md border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white px-3 py-2 mb-3"
          ></textarea>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              (click)="showCancelDialog = false"
              class="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-700"
            >
              Cerrar
            </button>
            <button
              type="button"
              (click)="aplicarCancel()"
              [disabled]="!cancelReasonId"
              class="px-3 py-1.5 text-sm rounded-md bg-error-500 text-white disabled:opacity-50"
            >
              Cancelar tarea
            </button>
          </div>
        </div>
      </div>
    }
  }
</app-modal>
